import { useMemo } from "react";
import InlineEditableDetail from "../InlineEdit/InlineEditableDetail";
import { useInlineFieldEdit } from "../InlineEdit/useInlineFieldEdit";
import useLeadSource from "@/features/sourceAndCampaign/api/useLeadSource";
import { useCampaigns } from "@/features/sourceAndCampaign/api/useCampaigns";

/**
 * Inline-editable Source + Campaign cells.
 *
 * Split into its own component so the option queries (`lead-sources`,
 * `campaigns`) only mount for users who can actually edit — a read-only
 * viewer opening the drawer pays nothing. Both are cached React-Query keys
 * the filter panel already warms, so in practice this is a cache read.
 */
export default function LeadSourceInlineFields({
  sourceName,
  sourceId,
  campaignName,
  campaignId,
  showSource = true,
  showCampaign = true,
}: {
  sourceName?: string;
  sourceId?: number | string | null;
  campaignName?: string;
  campaignId?: number | string | null;
  showSource?: boolean;
  showCampaign?: boolean;
}) {
  const { lockReason, saveLeadFields } = useInlineFieldEdit();
  const { sourceSelects } = useLeadSource();
  const { campaigns } = useCampaigns({ isActive: true });

  const campaignOptions = useMemo(
    () =>
      (campaigns ?? []).map((c: any) => ({
        value: String(c.id),
        label: c.name,
      })),
    [campaigns],
  );

  const sourceOptions = useMemo(
    () =>
      (sourceSelects ?? []).map((s: { value: string; name: string }) => ({
        value: s.value,
        label: s.name,
      })),
    [sourceSelects],
  );

  return (
    <>
      {showSource && (
        <InlineEditableDetail
          label="Source"
          value={sourceName}
          lockedReason={lockReason("source:leadSource")}
          editor={{
            kind: "select",
            value: sourceId != null ? String(sourceId) : "",
            options: sourceOptions,
          }}
          onSave={(next: string) => saveLeadFields({ leadSourceId: next })}
        />
      )}

      {showCampaign && (
        <InlineEditableDetail
          label="Campaign"
          value={campaignName}
          lockedReason={lockReason("source:campaign")}
          editor={{
            kind: "select",
            value: campaignId != null ? String(campaignId) : "",
            options: campaignOptions,
          }}
          onSave={(next: string) => saveLeadFields({ campaignId: next })}
        />
      )}
    </>
  );
}
